import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, LogOut, Loader2, RefreshCw, Mail, Phone, MapPin, Calendar, ArrowLeft, CreditCard, CheckCircle2, Link2, ChevronDown, ChevronRight, ShieldCheck, AlertTriangle, MailX, MailWarning, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TOKEN_KEY = "totehaul_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);

  // Verify existing token on mount
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .get("/admin/me", { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success("Signed out");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF4F00]" />
      </div>
    );
  }

  if (!token) {
    return <LoginForm onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
}

const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      onLogin(data.token);
      toast.success("Welcome back");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-md bg-white border border-neutral-200 p-8 md:p-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#FF4F00] mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#FF4F00] flex items-center justify-center">
            <Box className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">TOTEHAUL · ADMIN</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Sign in</h1>
        <p className="text-neutral-500 mt-2 text-sm">Enter your admin password to view bookings.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-neutral-500 font-semibold">Password</label>
            <input
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="mt-2 w-full border border-neutral-200 px-4 py-3 bg-white focus:outline-none focus:border-[#FF4F00] focus:ring-1 focus:ring-[#FF4F00]"
            />
          </div>
          {err && (
            <p className="text-sm text-red-600 font-medium" data-testid="admin-login-error">{err}</p>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-submit"
            className="w-full bg-[#FF4F00] text-white px-6 py-4 font-semibold tracking-wide hover:bg-[#E64700] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  confirmed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  pending_payment: "bg-amber-50 text-amber-700 border border-amber-200",
  cancelled: "bg-neutral-100 text-neutral-500 border border-neutral-200",
};

const Dashboard = ({ token, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | confirmed | pending_payment
  const [chargingCode, setChargingCode] = useState(null);
  const [expandedCode, setExpandedCode] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings", { headers: authHeader });
      setBookings(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired — please sign in again");
        onLogout();
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  const chargeBalance = async (code) => {
    if (!window.confirm(`Charge remaining balance for ${code}? This will attempt to auto-charge the saved card.`)) return;
    setChargingCode(code);
    try {
      const { data } = await api.post(`/admin/bookings/${code}/charge-balance`, {}, { headers: authHeader });
      toast.success(`Balance charged for ${code}`);
      setBookings((prev) => prev.map((b) => (b.confirmation_code === code ? data : b)));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Charge failed";
      toast.error(msg);
      // Refresh so we pick up any status change (requires_action / failed)
      fetchBookings();
    } finally {
      setChargingCode(null);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []); // fetchBookings closes over stable token; only run once on mount

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = {
    all: bookings.length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    pending_payment: bookings.filter((b) => b.status === "pending_payment").length,
  };
  const revenueConfirmed = bookings
    .filter((b) => b.status === "confirmed")
    .reduce((s, b) => s + (b.deposit || 0), 0);
  const balancePaid = bookings
    .filter((b) => b.balance_status === "paid")
    .reduce((s, b) => s + (b.balance_due || 0), 0);
  const balanceOutstanding = bookings
    .filter((b) => b.status === "confirmed" && b.balance_status !== "paid")
    .reduce((s, b) => s + (b.balance_due || 0), 0);
  const addonRevenue = bookings.reduce((s, b) => s + (b.addon_total || 0), 0);

  const copyUpsellLink = (code) => {
    const url = `${window.location.origin}/upsell/${code}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => toast.success(`Upsell link copied: ${url}`),
        () => toast.error("Copy failed — link: " + url)
      );
    } else {
      toast.success("Link: " + url);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#FF4F00] flex items-center justify-center">
              <Box className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">TOTEHAUL · ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchBookings}
              data-testid="admin-refresh"
              className="text-sm text-neutral-300 hover:text-white inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="text-sm text-neutral-300 hover:text-white inline-flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">Dashboard</span>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Bookings</h1>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <Stat label="Confirmed" value={counts.confirmed} testid="stat-confirmed" />
          <Stat label="Pending payment" value={counts.pending_payment} testid="stat-pending" />
          <Stat label="Deposits + balances collected" value={`$${(revenueConfirmed + balancePaid).toLocaleString()}`} testid="stat-revenue" />
          <Stat label="Balance outstanding" value={`$${balanceOutstanding.toLocaleString()}`} testid="stat-balance" />
          <Stat label="Add-on revenue" value={`$${addonRevenue.toLocaleString()}`} testid="stat-addons" />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 border-b border-neutral-200 pb-2">
          {[
            { k: "all", label: `All (${counts.all})` },
            { k: "confirmed", label: `Confirmed (${counts.confirmed})` },
            { k: "pending_payment", label: `Pending (${counts.pending_payment})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              data-testid={`filter-${t.k}`}
              className={`px-4 py-2 text-sm font-semibold tracking-wide transition-colors ${
                filter === t.k
                  ? "bg-[#0A0A0A] text-white"
                  : "text-neutral-600 hover:text-[#FF4F00]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          {loading && bookings.length === 0 ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#FF4F00]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-neutral-500" data-testid="empty-state">
              No bookings yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <Th>Code</Th>
                  <Th>Status</Th>
                  <Th>Tier</Th>
                  <Th>Window</Th>
                  <Th>Customer</Th>
                  <Th>Contact</Th>
                  <Th>Address</Th>
                  <Th className="text-right">Deposit</Th>
                  <Th className="text-right">Balance</Th>
                  <Th className="text-right">Add-ons</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const canCharge =
                    b.status === "confirmed" &&
                    b.balance_status !== "paid" &&
                    !!b.stripe_customer_id &&
                    !!b.stripe_payment_method_id &&
                    (b.balance_due || 0) > 0;
                  const hasAddons = (b.addons || []).length > 0;
                  const isExpanded = expandedCode === b.confirmation_code;
                  return (
                    <React.Fragment key={b.confirmation_code}>
                      <tr className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`row-${b.confirmation_code}`}>
                        <Td className="font-mono text-xs">
                          {hasAddons ? (
                            <button
                              onClick={() => setExpandedCode(isExpanded ? null : b.confirmation_code)}
                              data-testid={`toggle-addons-${b.confirmation_code}`}
                              className="inline-flex items-center gap-1 hover:text-[#FF4F00]"
                            >
                              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              {b.confirmation_code}
                            </button>
                          ) : (
                            <span className="pl-4">{b.confirmation_code}</span>
                          )}
                        </Td>
                        <Td>
                          <span className={`inline-block text-xs tracking-wide uppercase font-semibold px-2 py-1 ${STATUS_STYLES[b.status] || ""}`}>
                            {b.status.replace("_", " ")}
                          </span>
                        </Td>
                        <Td>{b.tier_name} <span className="text-neutral-400">· {b.tote_count} totes</span></Td>
                        <Td>
                          <span className="inline-flex items-center gap-1.5 text-neutral-700">
                            <Calendar className="w-3.5 h-3.5 text-neutral-400" />
                            {b.start_date} → {b.end_date}
                          </span>
                        </Td>
                        <Td className="font-semibold">
                          <div className="flex items-center gap-2">
                            {b.full_name}
                            {b.damage_protection && (
                              <ShieldCheck
                                className="w-3.5 h-3.5 text-emerald-600 shrink-0"
                                aria-label="Damage protection active"
                              />
                            )}
                          </div>
                        </Td>
                        <Td>
                          <div className="flex flex-col gap-0.5 text-xs">
                            <span className="inline-flex items-center gap-1.5 text-neutral-700">
                              <Mail className="w-3 h-3 text-neutral-400" />{b.email}
                              <EmailStatusBadge
                                status={b.confirmation_email_status}
                                kind="conf"
                                code={b.confirmation_code}
                              />
                              <EmailStatusBadge
                                status={b.reminder_email_status}
                                kind="rem"
                                code={b.confirmation_code}
                              />
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-neutral-700"><Phone className="w-3 h-3 text-neutral-400" />{b.phone}</span>
                          </div>
                        </Td>
                        <Td className="text-xs">
                          <span className="inline-flex items-start gap-1.5 text-neutral-700">
                            <MapPin className="w-3 h-3 text-neutral-400 mt-0.5" />
                            {b.delivery_address}
                          </span>
                        </Td>
                        <Td className="text-right whitespace-nowrap font-semibold">
                          ${b.deposit || 0}
                        </Td>
                        <Td className="text-right whitespace-nowrap">
                          <BalanceCell
                            booking={b}
                            canCharge={canCharge}
                            charging={chargingCode === b.confirmation_code}
                            onCharge={() => chargeBalance(b.confirmation_code)}
                          />
                        </Td>
                        <Td className="text-right whitespace-nowrap">
                          <AddonsCell
                            booking={b}
                            canLink={b.status === "confirmed" && !!b.stripe_customer_id}
                            onCopy={() => copyUpsellLink(b.confirmation_code)}
                          />
                        </Td>
                      </tr>
                      {isExpanded && hasAddons && (
                        <tr className="bg-[#FF4F00]/5 border-b border-neutral-200" data-testid={`addons-detail-${b.confirmation_code}`}>
                          <td colSpan={10} className="px-8 py-6">
                            <p className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold mb-4">
                              Add-ons purchased ({b.addons.length} order{b.addons.length > 1 ? "s" : ""})
                            </p>
                            <div className="space-y-4">
                              {b.addons.map((batch, i) => (
                                <div key={batch.charge_id || i} className="bg-white border border-neutral-200 p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-neutral-500 font-mono">{batch.charge_id}</span>
                                    <span className="text-xs text-neutral-500">
                                      {batch.charged_at ? new Date(batch.charged_at).toLocaleString() : ""}
                                    </span>
                                  </div>
                                  <ul className="space-y-1 text-sm">
                                    {batch.items.map((item, j) => (
                                      <li key={j} className="flex justify-between text-neutral-700">
                                        <span>{item.quantity}× {item.name} <span className="text-neutral-400">@ ${item.unit_price}</span></span>
                                        <span className="font-semibold">${item.line_total}</span>
                                      </li>
                                    ))}
                                    <li className="flex justify-between pt-2 border-t border-neutral-200 mt-2 text-[#0A0A0A]">
                                      <span className="font-semibold">Order total</span>
                                      <span className="font-display font-bold">${batch.amount}</span>
                                    </li>
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-neutral-200 p-5" data-testid={testid}>
    <p className="text-xs tracking-[0.2em] uppercase text-neutral-500 font-semibold">{label}</p>
    <p className="font-display text-3xl font-bold mt-2">{value}</p>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs tracking-[0.15em] uppercase text-neutral-500 font-semibold ${className}`}>{children}</th>
);
const AddonsCell = ({ booking: b, canLink, onCopy }) => (
  <div className="inline-flex flex-col items-end gap-1.5">
    {b.addon_total > 0 ? (
      <span className="font-semibold text-emerald-700" data-testid={`addon-total-${b.confirmation_code}`}>
        + ${b.addon_total}
      </span>
    ) : (
      <span className="text-neutral-400 text-xs">—</span>
    )}
    <button
      onClick={onCopy}
      disabled={!canLink}
      data-testid={`copy-upsell-${b.confirmation_code}`}
      title={canLink ? "Copy the customer's upsell link to send by text or email" : "Booking must be confirmed with a saved card"}
      className="inline-flex items-center gap-1.5 border border-neutral-300 text-neutral-700 text-xs font-semibold px-3 py-1.5 tracking-wide hover:border-[#FF4F00] hover:text-[#FF4F00] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-neutral-300 disabled:hover:text-neutral-700"
    >
      <Link2 className="w-3 h-3" /> Copy upsell link
    </button>
  </div>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-4 align-top ${className}`}>{children}</td>
);

const BalanceCell = ({ booking: b, canCharge, charging, onCharge }) => {
  if (b.balance_status === "paid") {
    return (
      <div className="inline-flex flex-col items-end gap-1" data-testid={`balance-paid-${b.confirmation_code}`}>
        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold uppercase tracking-wide">
          <CheckCircle2 className="w-3.5 h-3.5" /> ${b.balance_due} charged
        </span>
        {b.balance_charged_at && (
          <span className="text-[10px] text-neutral-400">
            {new Date(b.balance_charged_at).toLocaleDateString()}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="inline-flex flex-col items-end gap-1.5">
      <span className="text-neutral-500 text-xs">${b.balance_due || 0} due</span>
      <button
        onClick={onCharge}
        disabled={!canCharge || charging}
        data-testid={`charge-balance-${b.confirmation_code}`}
        title={canCharge ? "Charge the saved card for the remaining balance" : "Not chargeable yet (deposit must be paid and card must be saved)"}
        className="inline-flex items-center gap-1.5 bg-[#FF4F00] text-white text-xs font-semibold px-3 py-1.5 tracking-wide hover:bg-[#E64700] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#FF4F00]"
      >
        {charging ? (
          <><Loader2 className="w-3 h-3 animate-spin" /> Charging…</>
        ) : (
          <><CreditCard className="w-3 h-3" /> Charge balance</>
        )}
      </button>
      {b.balance_status === "failed" && b.balance_error && (
        <span className="text-[10px] text-red-600 max-w-[180px] text-right leading-tight">
          Last attempt: {b.balance_error}
        </span>
      )}
      {b.balance_status === "requires_action" && (
        <span className="text-[10px] text-amber-600 text-right leading-tight">
          Needs customer re-auth
        </span>
      )}
    </div>
  );
};

// Small pill next to the customer email showing Resend delivery status.
// kind: "conf" (booking confirmation) or "rem" (delivery reminder)
const EMAIL_BADGE_CONFIG = {
  delivered:        { icon: MailCheck,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "delivered" },
  opened:           { icon: MailCheck,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "opened" },
  clicked:          { icon: MailCheck,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "clicked" },
  sent:             { icon: Mail,        cls: "bg-neutral-50 text-neutral-600 border-neutral-200", label: "sent" },
  delivery_delayed: { icon: MailWarning, cls: "bg-amber-50 text-amber-700 border-amber-200",       label: "delayed" },
  bounced:          { icon: MailX,       cls: "bg-red-50 text-red-700 border-red-200",             label: "bounced" },
  complained:       { icon: AlertTriangle, cls: "bg-red-50 text-red-700 border-red-200",           label: "spam" },
  failed:           { icon: MailX,       cls: "bg-red-50 text-red-700 border-red-200",             label: "failed" },
};

const EmailStatusBadge = ({ status, kind, code }) => {
  if (!status) return null;
  const cfg = EMAIL_BADGE_CONFIG[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const label = kind === "rem" ? `reminder: ${cfg.label}` : `confirmation: ${cfg.label}`;
  return (
    <span
      title={label}
      data-testid={`email-status-${kind}-${code}`}
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${cfg.cls}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
};

